import { Capacitor } from '@capacitor/core'
import * as Sentry from '@sentry/browser'
import {
  buildDefaultHeaders,
  checkNativePushPermission,
  getCachedNativeToken,
  registerNativePush,
  unregisterNativePush
} from '../native/push'
import { registerWebPush, unregisterWebPush } from '../webpush'

export default (options = {}) => ({
  // State
  enabled: false,
  supported: true,
  loading: false,
  permission: (typeof Notification !== 'undefined') ? Notification.permission : null,

  // Translated user-facing texts from the backend: { title, denied, blocked, error }
  messages: options.messages ?? {},

  async init() {
    try {
      if (Capacitor.isNativePlatform()) {
        this.supported = true

        // Device truth: permission granted + this device's token active server-side.
        const permission = await checkNativePushPermission()
        const token = getCachedNativeToken()

        this.enabled = permission === 'granted' &&
          !!token &&
          await this.isActiveOnServer({ fcm_token: token })
        return
      }

      // Browser (WebPush): local subscription must also still exist server-side.
      this.supported = ('serviceWorker' in navigator) && ('PushManager' in window)
      if (!this.supported) {
        return
      }

      const reg = await navigator.serviceWorker.ready
      const existingSub = await reg.pushManager.getSubscription()

      this.enabled = !!existingSub &&
        await this.isActiveOnServer({ endpoint: existingSub.toJSON().endpoint })
    } catch (e) {
      Sentry.captureException(e)
    }
  },

  async isActiveOnServer(payload) {
    try {
      const res = await fetch('/user/push-status', {
        method: 'POST',
        headers: buildDefaultHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      })

      if (!res.ok) {
        throw new Error(`Push status check failed with status ${res.status} (${res.url})`)
      }

      return (await res.json()).active === true
    } catch (e) {
      // The status check must never break the menu — fall back to local knowledge.
      Sentry.captureException(e)
      return true
    }
  },

  toggle() {
    if (this.disabled) {
      return
    }
    document.querySelector('#web-push-notifications')
      .dispatchEvent(new Event('click'))
  },

  async onToggle() {
    if (!this.supported) {
      return
    }

    const isNative = Capacitor.isNativePlatform()

    this.loading = true
    try {
      if (this.enabled) {
        const result = isNative ? await registerNativePush() : await registerWebPush()

        if (result !== 'registered') {
          // User-actionable: permission denied or the browser blocks the push service.
          this.enabled = false
          this.notify(result === 'blocked' ? this.messages.blocked : this.messages.denied, 'warning')
        }
      } else {
        await (isNative ? unregisterNativePush() : unregisterWebPush())
      }

      this.permission = (typeof Notification !== 'undefined') ? Notification.permission : this.permission
    } catch (e) {
      // Systemic failure: revert the switch, tell the user, report to Sentry.
      this.enabled = !this.enabled
      Sentry.captureException(e)
      this.notify(this.messages.error, 'error')
    } finally {
      this.loading = false
    }
  },

  notify(message, type) {
    if (!message) {
      return
    }

    window.dispatchEvent(new CustomEvent('notify', {
      detail: {
        message,
        title: this.messages.title ?? '',
        type
      }
    }))
  },

  get disabled() {
    // Disable when not supported, permission explicitly denied (web), or during processing
    if (Capacitor.isNativePlatform()) {
      return this.loading === true
    }
    return !this.supported || this.permission === 'denied' || this.loading
  }
})
